import { create } from 'zustand';
import { isIncomingFor } from './call-wire';
import type { Call, CallParticipant, RawCallParticipant } from './call-view';
import { useAuthStore } from '@/store/auth-store';
import { sessionScope } from '@/features/auth/session-scope';

const pid = (p: RawCallParticipant) => p.user_id ?? p.userId ?? p.id;

/**
 * Client state for live calling (1:1 + group). TanStack Query owns history/active
 * snapshots; this store owns the ephemeral UX derived from checked native HTTP:
 *
 *   - `incoming` — a call ringing AT this user → the global IncomingCallModal.
 *   - `active`   — the call this user is currently in → the in-call CallRoom.
 *
 * Private call hints only request owned GET reconciliation; they never patch
 * prompts, terminal state or rosters. Native reads/explicit action receipts feed
 * this store, which clears synchronously on identity changes.
 */
interface CallState {
  revision: number;
  closedIds: string[];
  ended: (callId: string) => void;
  reconcileIncoming: (calls: Call[], userId: string, expectedRevision: number) => void;
  reconcileSnapshot: (call: Call | null, userId: string, expectedRevision: number) => void;
  accepted: (callId: string) => void;
  incoming: Call | null;
  active: Call | null;
  setIncoming: (call: Call) => void;
  clearIncoming: () => void;
  setActive: (call: Call) => void;
  clearActive: () => void;
  /** Legacy local helper; never consume private hint fields as participant DTOs. */
  participantJoined: (callId: string, participant: CallParticipant) => void;
  /** Legacy local helper; private hints reconcile the complete owned HTTP roster. */
  participantLeft: (callId: string, userId: string) => void;
  clear: () => void;
}

export const useCallStore = create<CallState>((set) => ({
  revision: 0,
  closedIds: [],
  ended: (callId) => set(s => ({ incoming: s.incoming?.id === callId ? null : s.incoming, active: s.active?.id === callId ? null : s.active, closedIds: [...s.closedIds.filter(id => id !== callId), callId].slice(-100), revision: s.revision + 1 })),
  incoming: null,
  active: null,
  reconcileSnapshot: (call, userId, expectedRevision) => set(s => {
    if (s.revision !== expectedRevision) return s;
    const active = s.active && call?.id === s.active.id ? call : null;
    const incoming = call && !s.closedIds.includes(call.id) && !active && isIncomingFor(call, userId) ? call : null;
    const closed = [s.active, s.incoming].filter(previous => previous && previous.id !== call?.id).map(previous => previous!.id);
    return { active, incoming, closedIds: [...s.closedIds, ...closed].slice(-100), revision: s.revision + 1 };
  }),
  reconcileIncoming: (calls, userId, expectedRevision) => set(s => {
    // A response initiated before a newer local state must not erase that state.
    if (s.revision !== expectedRevision) return s;
    const incoming = calls.find(call => !s.closedIds.includes(call.id) && isIncomingFor(call, userId)) ?? null;
    if (incoming && incoming.id === s.active?.id) return s;
    return { incoming, revision: s.revision + 1 };
  }),
  accepted: (callId) => set(s => s.active?.id === callId ? { active: { ...s.active, status: 'accepted' }, revision: s.revision + 1 } : s),
  setIncoming: (call) => set(s => s.active?.id === call.id || s.closedIds.includes(call.id) ? s : { incoming: { ...(s.incoming?.id === call.id ? s.incoming : {}), ...call }, revision: s.revision + 1 }),
  clearIncoming: () =>
    set((s) => (s.incoming ? { incoming: null, closedIds: [...s.closedIds, s.incoming.id].slice(-100), revision: s.revision + 1 } : s)),
  setActive: (call) => set(s => s.closedIds.includes(call.id) ? s : ({ active: call, incoming: null, revision: s.revision + 1 })),
  clearActive: () => set((s) => (s.active ? { active: null, revision: s.revision + 1 } : s)),
  participantJoined: (callId, participant) =>
    set((s) => {
      if (!s.active || s.active.id !== callId) return s;
      const current = s.active.participants ?? [];
      if (current.some((p) => pid(p) === participant.userId)) return s;
      // Store in the backend's raw shape so the roster stays one consistent type.
      const raw: RawCallParticipant = { user_id: participant.userId, user_name: participant.name };
      return { active: { ...s.active, participants: [...current, raw] } };
    }),
  participantLeft: (callId, userId) =>
    set((s) => {
      if (!s.active || s.active.id !== callId) return s;
      const current = s.active.participants ?? [];
      if (!current.some((p) => pid(p) === userId)) return s;
      return {
        active: { ...s.active, participants: current.filter((p) => pid(p) !== userId) },
      };
    }),
  clear: () => set(s => ({ incoming: null, active: null, closedIds: [], revision: s.revision + 1 })),
}));

// Clear synchronously, including a batched A→B→A switch. An effect after paint
// is too late to prevent the old prompt/room from appearing in the new account.
useAuthStore.subscribe((next, previous) => {
  if (sessionScope(next) !== sessionScope(previous)) useCallStore.getState().clear();
});
